import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Briefcase, Search, HardHat, ArrowRight, ArrowLeft, Upload } from "lucide-react";

const COPY = {
  es: {
    title: "¿Cómo quieres usar URBIS USA?",
    sub: "Elige una opción para personalizar tu experiencia. Podrás cambiarla más adelante.",
    business_t: "Soy un negocio",
    business_d: "Publica tu negocio en el directorio, gestiona clientes, facturas, proyectos y empleados.",
    client_t: "Busco servicios",
    client_d: "Encuentra profesionales de confianza para tu hogar, oficina o negocio en tu ciudad.",
    cta: "Continuar",
    biz_title: "Crea tu negocio",
    biz_sub: "Solo unos datos básicos para dejar tu perfil listo. Podrás completar el resto después.",
    name: "Nombre del negocio",
    category: "Categoría",
    city: "Ciudad",
    state: "Estado",
    logo: "Logo del negocio (opcional)",
    upload: "Subir logo",
    back: "Atrás",
    finish: "Crear y continuar",
  },
  en: {
    title: "How do you want to use URBIS USA?",
    sub: "Pick an option to personalize your experience. You can change it later.",
    business_t: "I'm a business",
    business_d: "List your business, manage clients, invoices, projects and employees.",
    client_t: "I'm looking for services",
    client_d: "Find trusted professionals for your home, office or business in your city.",
    cta: "Continue",
    biz_title: "Create your business",
    biz_sub: "Just a few basics to get your profile ready. You can complete the rest later.",
    name: "Business name",
    category: "Category",
    city: "City",
    state: "State",
    logo: "Business logo (optional)",
    upload: "Upload logo",
    back: "Back",
    finish: "Create and continue",
  },
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { setUserExternal } = useAuth();
  const { lang, catLabel, t } = useLang();
  const c = COPY[lang];
  const [step, setStep] = useState("role");
  const [choice, setChoice] = useState("business");
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [biz, setBiz] = useState({ name: "", category: "plumbing", custom_category: "", city: "", state: "", logo: "" });

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const continueRole = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-role", { role: choice });
      setUserExternal(data);
      if (choice === "business") {
        setStep("business");
      } else {
        toast.success("¡Listo!");
        navigate("/directory", { replace: true });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setBiz((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      set("logo", url);
      toast.success("Logo subido");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir el logo");
    }
  };

  const createBusiness = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/businesses", { ...biz, description: "", phone: "", email: "", website: "", address: "", cover_image: "", hourly_rate: 0 });
      toast.success("¡Negocio creado! Elige un plan para publicarlo.");
      navigate("/dashboard/subscription", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const Card = ({ value, Icon, title, desc }) => (
    <button
      type="button"
      onClick={() => setChoice(value)}
      className={`flex flex-col items-start gap-4 rounded-sm border-2 p-6 text-left transition-colors ${choice === value ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
      data-testid={`onboarding-${value}-card`}
    >
      <span className={`flex h-12 w-12 items-center justify-center rounded-sm ${choice === value ? "bg-primary text-white" : "bg-slate-100 text-secondary"}`}>
        <Icon className="h-6 w-6" />
      </span>
      <div>
        <h3 className="font-head text-lg font-bold text-secondary">{title}</h3>
        <p className="mt-1 text-sm text-slate-600">{desc}</p>
      </div>
    </button>
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-2xl">
        <div className="mb-8 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-white"><HardHat className="h-5 w-5" /></span>
          <span className="font-head text-xl font-extrabold text-secondary">{t("brand")}</span>
        </div>

        <div className="mb-8 flex items-center gap-3" data-testid="onboarding-progress">
          {[["role", lang === "es" ? "Tu perfil" : "Your profile"], ["business", lang === "es" ? "Tu negocio" : "Your business"]].map(([key, label], i) => {
            const active = step === key;
            const done = step === "business" && key === "role";
            return (
              <div key={key} className="flex flex-1 items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold ${active ? "bg-primary text-white" : done ? "bg-accent text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</span>
                  <span className={`text-sm font-semibold ${active ? "text-secondary" : "text-slate-400"}`}>{label}</span>
                </div>
                {i === 0 && <span className={`h-1 flex-1 rounded-full ${step === "business" ? "bg-accent" : "bg-slate-200"}`} />}
              </div>
            );
          })}
        </div>

        {step === "role" ? (
          <>
            <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary" data-testid="onboarding-title">{c.title}</h1>
            <p className="mt-2 text-slate-600">{c.sub}</p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Card value="business" Icon={Briefcase} title={c.business_t} desc={c.business_d} />
              <Card value="client" Icon={Search} title={c.client_t} desc={c.client_d} />
            </div>
            <button onClick={continueRole} disabled={loading} className="mt-8 flex items-center gap-2 rounded-sm bg-primary px-8 py-3 font-semibold text-white transition-colors hover:bg-orange-600 disabled:opacity-60" data-testid="onboarding-continue-btn">
              {loading ? "…" : c.cta} <ArrowRight className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <h1 className="font-head text-3xl font-extrabold tracking-tight text-secondary" data-testid="onboarding-business-title">{c.biz_title}</h1>
            <p className="mt-2 text-slate-600">{c.biz_sub}</p>
            <form onSubmit={createBusiness} className="mt-8 space-y-4 rounded-sm border border-slate-200 bg-white p-6" data-testid="onboarding-business-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.logo}</Label>
                <div className="mt-1.5 flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-sm border border-slate-200 bg-slate-50">
                    {biz.logo ? <img src={biz.logo} alt="logo" className="h-full w-full object-contain" /> : <Upload className="h-5 w-5 text-slate-300" />}
                  </div>
                  <label className="cursor-pointer rounded-sm border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-primary">
                    {c.upload}
                    <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="onboarding-logo-upload" />
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.name}</Label>
                <Input value={biz.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 rounded-sm" data-testid="onboarding-biz-name" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.category}</Label>
                <select value={biz.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm" data-testid="onboarding-biz-category">
                  {categories.map((cat) => <option key={cat.key} value={cat.key}>{catLabel(cat)}</option>)}
                </select>
                {biz.category === "other" && (
                  <Input value={biz.custom_category || ""} onChange={(e) => set("custom_category", e.target.value)} placeholder="Especifica tu categoría" className="mt-2 rounded-sm" data-testid="onboarding-custom-category" />
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.city}</Label>
                  <Input value={biz.city} onChange={(e) => set("city", e.target.value)} required className="mt-1.5 rounded-sm" data-testid="onboarding-biz-city" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.state}</Label>
                  <Input value={biz.state} onChange={(e) => set("state", e.target.value)} required placeholder="TX, FL, CA..." className="mt-1.5 rounded-sm" data-testid="onboarding-biz-state" />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button type="button" onClick={() => setStep("role")} className="flex items-center gap-1 rounded-sm border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:border-slate-300" data-testid="onboarding-back-btn">
                  <ArrowLeft className="h-4 w-4" /> {c.back}
                </button>
                <Button type="submit" disabled={loading} className="flex-1 rounded-sm bg-primary hover:bg-orange-600" data-testid="onboarding-create-biz-btn">
                  {loading ? "…" : <>{c.finish} <ArrowRight className="ml-1 h-4 w-4" /></>}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
